import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnInit,
  ElementRef,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  NgZone,
} from '@angular/core';
import { VimCoreLegacyWidgetsType } from '../../interface';
import { LEGACY_WIDGETS_GLOBAL_VARIABLE } from '../../const';
import _isEqual from 'lodash/isEqual';
import { VimCoreWidgetsService } from '../../service/widgets';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { from, Observable, timer } from 'rxjs';
import { mapTo, startWith, tap } from 'rxjs/operators';
import { logUat } from '@skyeng/libs/shared/device';

const OLD_WIDGETS_LOADED_TIMEOUT_MS = 300;

@DecorateUntilDestroy()
@Component({
  selector: 'vim-core-widget-loader',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'widgetLoader',
})
export class WidgetLoaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() widgetTag: string;
  @Input() inputs: Object = {};
  @Input() legacyType?: VimCoreLegacyWidgetsType;
  @Input() reloadOnChanges?: boolean;
  @Input() setInputsAsProps?: boolean;

  loaded$: Observable<boolean>;

  private widgetElement: HTMLElement | null = null;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private ngZone: NgZone,
    private vimCoreWidgetsService: VimCoreWidgetsService,
  ) {}

  ngOnInit(): void {
    const widgetType = this.legacyType ? this.legacyType : 'main';

    // custom elements can be undefined for old browsers
    const loaded$: Observable<unknown> = window.customElements
      ? from(customElements.whenDefined(this.widgetTag))
      : timer(OLD_WIDGETS_LOADED_TIMEOUT_MS);

    this.loaded$ = loaded$.pipe(mapTo(true), startWith(false));

    logUat('loading widget for tag ', this.widgetTag);

    this.vimCoreWidgetsService
      .load(widgetType)
      .pipe(
        tap(() => logUat('widget loaded for tag ', this.widgetTag)),
        takeUntilDestroyed(this),
      )
      .subscribe(() => this.createWidget());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.inputs &&
      this.widgetElement &&
      // required due to inputs object reference update on each CD tick if it is defined in template
      // e.x. `[inputs]="{ project: 'vimbox' }"`
      !_isEqual(changes.inputs.previousValue, changes.inputs.currentValue)
    ) {
      this.setWidgetInputs(this.widgetElement, this.inputs);

      if (this.reloadOnChanges) {
        this.reload();
      } else {
        this.reloadLegacyWidget();
      }
    }
  }

  ngOnDestroy(): void {
    this.widgetElement = null;
  }

  reload(): void {
    this.clearWidgetContainer();
    this.createWidget();
  }

  private createWidget(): void {
    this.widgetElement = document.createElement(this.widgetTag);

    this.setWidgetInputs(this.widgetElement, this.inputs);

    this.elementRef.nativeElement.appendChild(this.widgetElement);

    this.reloadLegacyWidget();

    logUat('widget created for tag ', this.widgetTag);
  }

  private setWidgetInputs(widgetElement: HTMLElement, inputs: Object): void {
    Object.keys(inputs).forEach(name => {
      if (this.setInputsAsProps) {
        // eslint-disable-next-line functional/immutable-data
        widgetElement[name] = this.inputs[name];
      } else {
        const normalizedName = this.kebabToCamelCase(name);

        widgetElement.setAttribute(normalizedName, this.inputs[name]);
      }
    });
  }

  private reloadLegacyWidget(): void {
    const widgetGlobalVariable = this.legacyType ? LEGACY_WIDGETS_GLOBAL_VARIABLE[this.legacyType] : undefined;

    if (!widgetGlobalVariable || !window[widgetGlobalVariable] || !window[widgetGlobalVariable].reload) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      window[widgetGlobalVariable].reload();
    });
  }

  private kebabToCamelCase(value: string): string {
    return value.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  private clearWidgetContainer(): void {
    // eslint-disable-next-line functional/immutable-data
    this.elementRef.nativeElement.innerHTML = '';
  }
}
